<template>
  <div class="w-full flex flex-col gap-6">
    <slot name="header" />
    <div
      v-for="feature in features"
      :key="feature.key"
      class="flex items-center gap-3 -mx-2 -my-1.5 px-2 py-1.5 rounded-lg transition-colors hover:bg-neutral-700"
      @mouseenter="showHoverCard(feature, $event)"
      @mouseleave="hideHoverCard"
    >
      <div
        class="w-5 h-5 shrink-0 overflow-hidden flex items-center justify-center rounded-md"
        :class="feature.imgUrl ? '' : 'bg-neutral-alpha-100'"
      >
        <img
          v-if="feature.imgUrl"
          :src="feature.imgUrl"
          :alt="feature.label"
          class="w-5 h-5 object-cover"
        >
        <component
          :is="resolveIcon(feature)"
          v-else
          :width="feature.iconSize"
          :height="feature.iconSize"
          class="text-white"
        />
      </div>
      <BaseText
        type="label"
        size="sm"
        class="flex-1 text-white"
      >
        {{ feature.label }}
      </BaseText>
      <BaseText
        v-if="feature.value"
        type="body"
        size="sm"
        class="text-neutral-alpha-650 whitespace-nowrap text-right"
      >
        {{ feature.value }}
      </BaseText>
      <CheckmarkIcon
        v-else
        :width="20"
        :height="20"
        class="text-white shrink-0"
      />
    </div>
    <transition name="fade">
      <div
        v-if="hoveredFeature"
        ref="hoverCard"
        class="fixed pointer-events-none"
        :style="hoverCardStyle"
      >
        <PlanFeatureHoverCard
          :size="hoveredFeature.hover.size"
          :title="hoveredFeature.hover.title"
          :description="hoveredFeature.hover.description"
          :value="hoveredFeature.value"
          :img-url="hoveredFeature.imgUrl"
          :icon="resolveIcon(hoveredFeature)"
          :icon-size="hoveredFeature.iconSize"
          :image="hoveredFeature.hover.image"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import PlanFeatureHoverCard from '@/components/account/subscription/PlanFeatureHoverCard.vue'
import CheckmarkIcon from '@/components/globals/Icons/CheckmarkIcon.vue'
import ChromeColorIcon from '@/components/globals/Icons/OnboardIcons/ChromeColorIcon.vue'
import MCPLogoIcon from '@/components/globals/Icons/MCPLogoIcon.vue'
import InstagramIcon from '@/components/globals/Icons/FilterIcons/InstagramIcon.vue'
import { getPlanFeatures } from '@/utils/stripe/productMeta'

const HOVER_CARD_GAP = 8
const VIEWPORT_MARGIN = 8

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

export default {
  name: 'PlanFeatureList',
  components: {
    PlanFeatureHoverCard,
    CheckmarkIcon,
    ChromeColorIcon,
    MCPLogoIcon,
    InstagramIcon
  },
  props: {
    planKey: {
      type: String,
      required: true
    },
    isMonthly: {
      type: Boolean,
      default: true
    },
    baseLens: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      hoveredFeature: null,
      hoverCardStyle: {},
      iconMap: {
        mcp: MCPLogoIcon,
        chrome: ChromeColorIcon,
        instagram: InstagramIcon
      }
    }
  },
  computed: {
    features () {
      return getPlanFeatures(this.planKey, {
        isMonthly: this.isMonthly,
        baseLens: this.baseLens
      })
    }
  },
  mounted () {
    window.addEventListener('scroll', this.hideHoverCard, true)
    window.addEventListener('resize', this.hideHoverCard)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.hideHoverCard, true)
    window.removeEventListener('resize', this.hideHoverCard)
  },
  methods: {
    async showHoverCard (feature, event) {
      if (!feature.hover) return
      const rowRect = event.currentTarget.getBoundingClientRect()
      const width = Math.min(rowRect.width, document.documentElement.clientWidth - VIEWPORT_MARGIN * 2)

      // Render the card invisibly first so we can measure its height before placing it
      this.hoveredFeature = feature
      this.hoverCardStyle = { width: `${width}px`, top: '0px', left: '0px', visibility: 'hidden', zIndex: 1000 }
      await this.$nextTick()
      if (this.hoveredFeature !== feature || !this.$refs.hoverCard) return

      const { top, left } = this.getHoverCardPosition(rowRect, width, this.$refs.hoverCard.offsetHeight)
      this.hoverCardStyle = { ...this.hoverCardStyle, top: `${top}px`, left: `${left}px`, visibility: 'visible' }
    },
    hideHoverCard () {
      this.hoveredFeature = null
    },
    getHoverCardPosition (rowRect, width, height) {
      const viewportWidth = document.documentElement.clientWidth
      const viewportHeight = document.documentElement.clientHeight
      const maxTop = viewportHeight - height - VIEWPORT_MARGIN

      // Place below the row, or above if it doesn't fit below and there's more space above
      const spaceBelow = viewportHeight - rowRect.bottom - HOVER_CARD_GAP - VIEWPORT_MARGIN
      const spaceAbove = rowRect.top - HOVER_CARD_GAP - VIEWPORT_MARGIN
      const placeBelow = spaceBelow >= height || spaceBelow >= spaceAbove
      return {
        left: clamp(rowRect.left, VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
        top: clamp(placeBelow ? rowRect.bottom + HOVER_CARD_GAP : rowRect.top - HOVER_CARD_GAP - height, VIEWPORT_MARGIN, maxTop)
      }
    },
    resolveIcon (feature) {
      if (feature.icon && typeof feature.icon !== 'string') return feature.icon
      return this.iconMap[feature.icon]
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 150ms ease-in-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
